import { Component, OnInit } from '@angular/core';
import { ICampaignPublishInfo, IPublishInfo } from '@studio/domain/campaign';
import { CreativesetDataService } from '@app/shared/creativeset';
import { CampaignService } from '@app/shared/services';
import { UserService } from '@app/shared/user/state/user.service';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { Logger } from '@studio/utils/logger';
import { Subject, Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EditCreativeService, PublishService } from '../services';
import { ChangeState } from '../services/creative-mutation';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

export enum CampaignActionState {
    List,
    Push,
    Create
}

@Component({
    selector: 'campaign-actions',
    templateUrl: './campaign-actions.component.html',
    styleUrls: ['./campaign-actions.component.scss']
})
export class CampaignActionsComponent implements OnInit {
    campaigns: ICampaignPublishInfo[] = [];
    isPublishing: boolean;
    state: CampaignActionState;

    campaignsIsOpen = false;
    CampaignActionState = CampaignActionState;

    private logger = new Logger('CampaignActions', true);
    private publishedCreatives: IPublishInfo[] = [];

    private unsubscribe$ = new Subject<void>();
    publishServicetimeout: number;
    hasChangesToPublish: boolean;
    hasConnectedCampaigns: boolean;
    private creativeset: ICreativeset;

    addToCampaignDisabled: boolean;
    addToCampaignText: string;

    hasSocialCampaignPermissions: boolean;

    isUpdatingChecksums$: Observable<boolean>;

    constructor(
        private creativesetDataService: CreativesetDataService,
        private publishService: PublishService,
        private campaignService: CampaignService,
        private editCreativeService: EditCreativeService,
        private userService: UserService
    ) {
        this.isUpdatingChecksums$ = this.editCreativeService.isUpdatingChecksums$;
        this.creativesetDataService.creativeset$
            .pipe(takeUntilDestroyed())
            .subscribe(creativeset => (this.creativeset = creativeset));

        this.publishService.publish.change$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(({ state }) => {
                if (state === ChangeState.PENDING) {
                    this.isPublishing = true;
                } else if (state === ChangeState.FINISHED) {
                    this.isPublishing = false;

                    clearTimeout(this.publishServicetimeout);
                    this.publishServicetimeout = window.setTimeout(() => {
                        this.parseCampaigns();
                    }, 2000);
                }
                this.setCampaignActionState();
            });

        this.editCreativeService.updatedCreativeTargetUrl$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.parseCampaigns());

        this.campaignService.creativesPublished$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(creativesPublishInfo => {
                this.publishedCreatives = creativesPublishInfo;
                this.parseCampaigns();
            });
    }

    async ngOnInit(): Promise<void> {
        this.hasSocialCampaignPermissions =
            await this.userService.hasPermission('SocialCampaignManager');
    }

    onDropdownOpenToggle(open: boolean): void {
        this.campaignsIsOpen = open;
    }

    private parseCampaigns(): void {
        this.hasChangesToPublish = false;
        this.campaigns = this.campaignService.getCampaigns();

        this.hasChangesToPublish = this.publishedCreatives.some(publishInfo => {
            if (this.publishService.isPublishable(publishInfo.creative)) {
                return true;
            } else {
                return false;
            }
        });

        this.setCampaignActionState();
        this.hasConnectedCampaigns = this.hasAnyConnectedCampaigns();
    }

    private hasAnyConnectedCampaigns(): boolean {
        const creatives = [...this.creativeset.creatives];
        if (creatives) {
            return creatives.some(creative =>
                this.campaignService.isCreativeConnectedToCampaign(creative)
            );
        }
        return false;
    }

    private setCampaignActionState(): void {
        if (!this.hasAnyConnectedCampaigns()) {
            this.state = CampaignActionState.Create;
        } else if (this.hasChangesToPublish) {
            this.state = CampaignActionState.Push;
        } else if (!this.isPublishing) {
            this.state = CampaignActionState.List;
        }
    }

    onCreateCampaign(): void {
        if (this.hasSocialCampaignPermissions) {
            return;
        }
        this.campaignService.redirectToCreateCampaign();
    }

    onOpenCampaign(campaignId: string): void {
        this.campaignService.openCampaign(campaignId);
    }

    onPublishCreatives(): void {
        if (!this.isPublishing) {
            this.publishService.publishCreatives();
        }
    }

    redirectToCreateCampaign(): void {
        this.campaignService.redirectToCreateCampaign();
    }
}
